import {
  AppWindow,
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  Cloud,
  Cpu,
  Database,
  ExternalLink,
  Gamepad2,
  Gauge,
  HardDrive,
  Layers3,
  MemoryStick,
  MonitorPlay,
  RefreshCw,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Tv,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";
import guideOgImage from "@/assets/guides/stb-ram-storage-og.jpg";
import gamingLifestyleImage from "@/assets/remote-app/lifestyle/inext-remote-app-gamepad-lifestyle.jpg";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localeByCode } from "@/lib/locale-registry";
import { localizeUrl } from "@/lib/site";

const GUIDE_PUBLISHED_DATE = "2026-07-15";
const GUIDE_UPDATED_DATE = "2026-07-15";

type Workload = {
  icon: LucideIcon;
  title: string;
  body: string;
  signal: string;
};

type Configuration = {
  memory: string;
  label: string;
  summary: string;
  fits: string[];
  watch: string;
  models: Array<{ name: string; href: string }>;
  featured?: boolean;
};

export function StbRamStorageGuidePage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");
  const locale = localeByCode(site.locale).intl;
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeZone: "UTC",
  });
  const publishedDate = dateFormatter.format(new Date(`${GUIDE_PUBLISHED_DATE}T00:00:00Z`));
  const updatedDate = dateFormatter.format(new Date(`${GUIDE_UPDATED_DATE}T00:00:00Z`));
  const showUpdatedDate = GUIDE_UPDATED_DATE !== GUIDE_PUBLISHED_DATE;

  const workloads: Workload[] = [
    {
      icon: Tv,
      title: messages.rsgWorkload1Title,
      body: messages.rsgWorkload1Body,
      signal: messages.rsgWorkload1Signal,
    },
    {
      icon: AppWindow,
      title: messages.rsgWorkload2Title,
      body: messages.rsgWorkload2Body,
      signal: messages.rsgWorkload2Signal,
    },
    {
      icon: Building2,
      title: messages.rsgWorkload3Title,
      body: messages.rsgWorkload3Body,
      signal: messages.rsgWorkload3Signal,
    },
    {
      icon: Gamepad2,
      title: messages.rsgWorkload4Title,
      body: messages.rsgWorkload4Body,
      signal: messages.rsgWorkload4Signal,
    },
    {
      icon: Cloud,
      title: messages.rsgWorkload5Title,
      body: messages.rsgWorkload5Body,
      signal: messages.rsgWorkload5Signal,
    },
    {
      icon: Layers3,
      title: messages.rsgWorkload6Title,
      body: messages.rsgWorkload6Body,
      signal: messages.rsgWorkload6Signal,
    },
  ];

  const ramFactors = [
    {
      icon: ServerCog,
      title: messages.rsgRamFactor1Title,
      body: messages.rsgRamFactor1Body,
    },
    {
      icon: AppWindow,
      title: messages.rsgRamFactor2Title,
      body: messages.rsgRamFactor2Body,
    },
    {
      icon: MonitorPlay,
      title: messages.rsgRamFactor3Title,
      body: messages.rsgRamFactor3Body,
    },
    {
      icon: Gamepad2,
      title: messages.rsgRamFactor4Title,
      body: messages.rsgRamFactor4Body,
    },
  ];

  const configurations: Configuration[] = [
    {
      memory: "1 GB / 8 GB",
      label: messages.rsgConfig1Label,
      summary: messages.rsgConfig1Summary,
      fits: [messages.rsgConfig1Fit1, messages.rsgConfig1Fit2, messages.rsgConfig1Fit3],
      watch: messages.rsgConfig1Watch,
      models: [{ name: "inext TV5", href: "/products/inext-tv5" }],
    },
    {
      memory: "2 GB / 16 GB",
      label: messages.rsgConfig2Label,
      summary: messages.rsgConfig2Summary,
      fits: [messages.rsgConfig2Fit1, messages.rsgConfig2Fit2, messages.rsgConfig2Fit3],
      watch: messages.rsgConfig2Watch,
      models: [
        { name: "inext TV5 Ultra", href: "/products/inext-tv5-ultra" },
        { name: "inext TV6", href: "/products/inext-tv6" },
      ],
      featured: true,
    },
    {
      memory: "4 GB / 32 GB",
      label: messages.rsgConfig3Label,
      summary: messages.rsgConfig3Summary,
      fits: [messages.rsgConfig3Fit1, messages.rsgConfig3Fit2, messages.rsgConfig3Fit3],
      watch: messages.rsgConfig3Watch,
      models: [{ name: "inext TV6 Ultra", href: "/products/inext-tv6-ultra" }],
    },
  ];

  const storageFactors = [
    {
      icon: Database,
      title: messages.rsgStorage1Title,
      body: messages.rsgStorage1Body,
    },
    {
      icon: RefreshCw,
      title: messages.rsgStorage2Title,
      body: messages.rsgStorage2Body,
    },
    {
      icon: AppWindow,
      title: messages.rsgStorage3Title,
      body: messages.rsgStorage3Body,
    },
    {
      icon: Gauge,
      title: messages.rsgStorage4Title,
      body: messages.rsgStorage4Body,
    },
  ];

  const optimizationItems = [
    { title: messages.rsgOptimize1Title, body: messages.rsgOptimize1Body },
    { title: messages.rsgOptimize2Title, body: messages.rsgOptimize2Body },
    { title: messages.rsgOptimize3Title, body: messages.rsgOptimize3Body },
    { title: messages.rsgOptimize4Title, body: messages.rsgOptimize4Body },
  ];

  const gamingTypes = [
    {
      title: messages.rsgGame1Title,
      body: messages.rsgGame1Body,
      emphasis: messages.rsgGame1Emphasis,
    },
    {
      title: messages.rsgGame2Title,
      body: messages.rsgGame2Body,
      emphasis: messages.rsgGame2Emphasis,
    },
    {
      title: messages.rsgGame3Title,
      body: messages.rsgGame3Body,
      emphasis: messages.rsgGame3Emphasis,
    },
  ];

  const tests = [
    messages.rsgTest1,
    messages.rsgTest2,
    messages.rsgTest3,
    messages.rsgTest4,
    messages.rsgTest5,
    messages.rsgTest6,
    messages.rsgTest7,
    messages.rsgTest8,
  ];

  const decisionItems: Array<{ icon: LucideIcon; title: string; body: string }> = [
    {
      icon: Wrench,
      title: messages.rsgDecision1Title,
      body: messages.rsgDecision1Body,
    },
    {
      icon: Cpu,
      title: messages.rsgDecision2Title,
      body: messages.rsgDecision2Body,
    },
    {
      icon: HardDrive,
      title: messages.rsgDecision3Title,
      body: messages.rsgDecision3Body,
    },
    {
      icon: Sparkles,
      title: messages.rsgDecision4Title,
      body: messages.rsgDecision4Body,
    },
  ];

  const faqs = [
    { q: messages.rsgFaqQ1, a: messages.rsgFaqA1 },
    { q: messages.rsgFaqQ2, a: messages.rsgFaqA2 },
    { q: messages.rsgFaqQ3, a: messages.rsgFaqA3 },
    { q: messages.rsgFaqQ4, a: messages.rsgFaqA4 },
    { q: messages.rsgFaqQ5, a: messages.rsgFaqA5 },
    { q: messages.rsgFaqQ6, a: messages.rsgFaqA6 },
  ];

  const canonical = new URL(u("/guides/stb-ram-and-storage"), site.origin).href;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: `${messages.rsgHeroTitle} ${messages.rsgHeroTitleAccent}`,
    description: messages.rsgSeoDescription,
    inLanguage: locale,
    mainEntityOfPage: canonical,
    image: new URL(guideOgImage, site.origin).href,
    datePublished: GUIDE_PUBLISHED_DATE,
    dateModified: GUIDE_UPDATED_DATE,
    author: { "@type": "Organization", name: "inext", url: site.origin },
    publisher: { "@type": "Organization", name: "inext", url: site.origin },
  };

  return (
    <SiteLayout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }}
      />

      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.rsgBreadcrumbPage}</span>
        </nav>
      </div>

      <section className="relative overflow-hidden border-b border-border/60 bg-muted/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-25"
        />
        <div className="mx-auto max-w-4xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-background px-3 py-1 text-xs text-muted-foreground">
            <MemoryStick aria-hidden className="h-3.5 w-3.5 text-primary" />
            {messages.rsgHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.rsgHeroTitle}{" "}
            <span className="text-gradient">{messages.rsgHeroTitleAccent}</span>
          </h1>
          <p
            className="mt-4 animate-fade-up text-xs text-muted-foreground"
            style={{ animationDelay: "140ms" }}
          >
            {messages.rsgPublishedLabel}{" "}
            <time dateTime={GUIDE_PUBLISHED_DATE}>{publishedDate}</time>
            {showUpdatedDate && (
              <>
                <span aria-hidden className="mx-2">
                  ·
                </span>
                {messages.rsgUpdatedLabel} <time dateTime={GUIDE_UPDATED_DATE}>{updatedDate}</time>
              </>
            )}
          </p>
          <p
            className="mx-auto mt-4 max-w-3xl animate-fade-up text-pretty text-lg leading-relaxed text-muted-foreground"
            style={{ animationDelay: "180ms" }}
          >
            {messages.rsgHeroSub}
          </p>
          <p
            className="mx-auto mt-5 max-w-2xl animate-fade-up text-sm font-medium text-foreground/80"
            style={{ animationDelay: "240ms" }}
          >
            {messages.rsgHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "300ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href="#comparison">{messages.rsgHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href={u("/products")}>{messages.rsgHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mx-auto mt-10 grid max-w-3xl animate-fade-up grid-cols-1 border-y border-border/70 sm:grid-cols-3 sm:divide-x sm:divide-border/70"
            style={{ animationDelay: "360ms" }}
          >
            {[
              ["1 / 8", messages.rsgStat1],
              ["2 / 16", messages.rsgStat2],
              ["4 / 32", messages.rsgStat3],
            ].map(([value, label]) => (
              <div key={value} className="px-5 py-4">
                <p className="text-lg font-semibold text-foreground">{value} GB</p>
                <p className="mt-1 text-xs text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgThesisEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgThesisTitle}
            </h2>
          </div>
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.rsgThesisBody1}</p>
            <p>{messages.rsgThesisBody2}</p>
            <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-5">
              <div className="flex items-start gap-3">
                <Zap aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground">{messages.rsgThesisNoteTitle}</h3>
                  <p className="mt-1 text-sm leading-relaxed">{messages.rsgThesisNoteBody}</p>
                </div>
              </div>
            </div>
            <p className="text-sm">
              {messages.rsgGoogleNote}{" "}
              <a
                href="https://source.android.com/docs/compatibility/cdd"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                {messages.rsgGoogleLink}
                <ExternalLink aria-hidden className="h-3.5 w-3.5" />
              </a>
            </p>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 bg-muted/20 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgWorkloadsEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgWorkloadsTitle}
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              {messages.rsgWorkloadsIntro}
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {workloads.map(({ icon: Icon, title, body, signal }) => (
              <article key={title} className="rounded-lg border border-border/70 bg-background p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon aria-hidden className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
                <p className="mt-4 border-t border-border/60 pt-4 text-xs font-semibold text-foreground/80">
                  {signal}
                </p>
              </article>
            ))}
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-medium uppercase tracking-wider text-primary">
                {messages.rsgRamEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.rsgRamTitle}
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{messages.rsgRamIntro}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {ramFactors.map(({ icon: Icon, title, body }) => (
                <article key={title} className="rounded-lg border border-border/70 p-5">
                  <Icon aria-hidden className="h-5 w-5 text-primary" />
                  <h3 className="mt-3 font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </RevealSection>

      <RevealSection
        id="comparison"
        className="border-b border-border/60 bg-muted/20 py-16 sm:py-20"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgConfigEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgConfigTitle}
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">{messages.rsgConfigIntro}</p>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {configurations.map((config) => (
              <article
                key={config.memory}
                className={`flex flex-col rounded-lg border bg-background p-6 ${
                  config.featured
                    ? "border-primary/40 shadow-[var(--shadow-soft)]"
                    : "border-border/70"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-2xl font-semibold text-foreground">{config.memory}</p>
                    <p className="mt-1 text-sm font-medium text-primary">{config.label}</p>
                  </div>
                  {config.featured ? (
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                      {messages.rsgConfigCommon}
                    </span>
                  ) : null}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                  {config.summary}
                </p>
                <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                  {messages.rsgConfigFitsLabel}
                </h3>
                <ul className="mt-3 space-y-2.5">
                  {config.fits.map((fit) => (
                    <li key={fit} className="flex gap-2.5 text-sm text-foreground/85">
                      <Check aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{fit}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 border-t border-border/60 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
                    {messages.rsgConfigWatchLabel}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {config.watch}
                  </p>
                </div>
                <div className="mt-auto pt-6">
                  <p className="text-xs text-muted-foreground">{messages.rsgConfigModelsLabel}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                    {config.models.map((model) => (
                      <a
                        key={model.name}
                        href={u(model.href)}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                      >
                        {model.name}
                        <ArrowRight aria-hidden className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-3xl text-center text-sm leading-relaxed text-muted-foreground">
            {messages.rsgConfigNote}
          </p>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgStorageEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgStorageTitle}
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">{messages.rsgStorageIntro}</p>
          </div>
          <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border/70 bg-border/70 sm:grid-cols-2 lg:grid-cols-4">
            {storageFactors.map(({ icon: Icon, title, body }) => (
              <article key={title} className="bg-background p-6">
                <Icon aria-hidden className="h-5 w-5 text-primary" />
                <h3 className="mt-4 font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
          <div className="mx-auto mt-8 max-w-4xl rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-5">
            <div className="flex items-start gap-3">
              <HardDrive
                aria-hidden
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400"
              />
              <p className="text-sm leading-relaxed text-foreground/80">
                {messages.rsgStorageNote}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 bg-foreground py-16 text-background sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgOptimizeEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgOptimizeTitle}
            </h2>
            <p className="mt-5 leading-relaxed text-background/70">{messages.rsgOptimizeIntro}</p>
            <div className="mt-6 rounded-lg border border-background/15 bg-background/[0.06] p-5">
              <p className="text-sm font-semibold text-background">
                {messages.rsgOptimizeProofTitle}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-background/70">
                {messages.rsgOptimizeProofBody}
              </p>
              <a
                href={u("/products/inext-tv5")}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                {messages.rsgOptimizeProofCta}
                <ArrowRight aria-hidden className="h-4 w-4" />
              </a>
            </div>
          </div>
          <ol className="grid gap-3 sm:grid-cols-2">
            {optimizationItems.map((item, index) => (
              <li
                key={item.title}
                className="rounded-lg border border-background/15 bg-background/[0.04] p-5"
              >
                <span className="text-xs font-semibold text-primary">0{index + 1}</span>
                <h3 className="mt-3 font-semibold text-background">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-background/65">{item.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid overflow-hidden rounded-lg border border-border/70 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="relative min-h-72 overflow-hidden bg-muted lg:min-h-[520px]">
              <img
                src={gamingLifestyleImage}
                alt={messages.rsgGamingImageAlt}
                width={1200}
                height={746}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="bg-background p-6 sm:p-10 lg:p-12">
              <p className="text-xs font-medium uppercase tracking-wider text-primary">
                {messages.rsgGamingEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.rsgGamingTitle}
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                {messages.rsgGamingIntro}
              </p>
              <div className="mt-8 space-y-6">
                {gamingTypes.map((game) => (
                  <article key={game.title} className="border-l-2 border-primary/30 pl-4">
                    <h3 className="font-semibold text-foreground">{game.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {game.body}
                    </p>
                    <p className="mt-2 text-xs font-semibold text-primary">{game.emphasis}</p>
                  </article>
                ))}
              </div>
              <p className="mt-7 rounded-lg bg-muted/50 p-4 text-sm leading-relaxed text-foreground/80">
                {messages.rsgGamingNote}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 bg-muted/20 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary">
                {messages.rsgTestEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.rsgTestTitle}
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{messages.rsgTestIntro}</p>
            </div>
            <ol className="grid gap-3 sm:grid-cols-2">
              {tests.map((test, index) => (
                <li
                  key={test}
                  className="flex gap-3 rounded-lg border border-border/70 bg-background p-4"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {index + 1}
                  </span>
                  <span className="pt-0.5 text-sm leading-relaxed text-foreground/85">{test}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-lg border border-primary/25 bg-primary/[0.04] p-5 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <ShieldCheck aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <h3 className="font-semibold text-foreground">{messages.rsgTestNoteTitle}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {messages.rsgTestNoteBody}
                </p>
              </div>
            </div>
            <Button
              asChild
              variant="outline"
              className="h-auto max-w-full shrink-0 whitespace-normal py-2 text-center leading-snug"
            >
              <a href={u("/guides/iptv-stb-buyers-checklist")}>
                {messages.rsgTestNoteCta}
                <ArrowRight aria-hidden className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgDecisionEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgDecisionTitle}
            </h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {decisionItems.map(({ icon: DecisionIcon, title, body }) => (
              <article key={title} className="flex gap-4 rounded-lg border border-border/70 p-5">
                <DecisionIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-8 text-center text-base font-medium text-foreground">
            {messages.rsgDecisionConclusion}
          </p>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 bg-muted/20 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-center text-xs font-medium uppercase tracking-wider text-primary">
            {messages.rsgFaqEyebrow}
          </p>
          <h2 className="mt-3 text-center text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.rsgFaqTitle}
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.q} value={`rsg-faq-${index}`}>
                <AccordionTrigger className="text-left text-base font-semibold">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      <RevealSection className="py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-lg border border-border/70 bg-foreground px-6 py-10 text-center text-background sm:px-10 sm:py-14">
            <MemoryStick aria-hidden className="mx-auto h-8 w-8 text-primary" />
            <p className="mt-5 text-xs font-medium uppercase tracking-wider text-primary">
              {messages.rsgCtaEyebrow}
            </p>
            <h2 className="mx-auto mt-3 max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.rsgCtaTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-background/70">
              {messages.rsgCtaBody}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg">
                <a href={salesCta}>{messages.rsgCtaPrimary}</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-background/25 bg-transparent text-background hover:bg-background/10 hover:text-background"
              >
                <a href={u("/products")}>{messages.rsgCtaSecondary}</a>
              </Button>
            </div>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
